/**
 * KL-1046 — Google reCAPTCHA legal disclosure.
 *
 * We hide the reCAPTCHA v3 badge globally (theme/foundations/styles.js). Google's terms only
 * permit hiding the badge when the disclosure below is shown in every form that runs reCAPTCHA:
 * https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
 *
 * Self-gating: renders nothing when reCAPTCHA is disabled/unconfigured for the site (same
 * GOOGLE_RECAPTCHA_ENABLED preference the provider + verify path use), so the notice never
 * appears on a form where reCAPTCHA isn't actually active.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {Link, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {isRecaptchaEnabledValue} from '@amblique/king-living/app/components/recaptcha/provider-utils';
import {RECAPTCHA_PREFERENCE_KEYS as KEYS} from '@amblique/king-living/app/constants/recaptcha';

export const RecaptchaDisclosure = ({linkProps = {}, ...textProps}) => {
  const enabled = isRecaptchaEnabledValue(useSitePreference(KEYS.ENABLED, false));

  if (!enabled) {
    return null;
  }

  return (
    <Text fontSize="xs" color="brandColors.greyscale70" {...textProps}>
      <FormattedMessage
        defaultMessage="This site is protected by reCAPTCHA and the Google <policy>Privacy Policy</policy> and <terms>Terms of Service</terms> apply."
        id="recaptcha.disclosure"
        values={{
          policy: (chunks) => (
            <Link
              href="https://policies.google.com/privacy"
              isExternal
              rel="noopener noreferrer"
              target="_blank"
              textDecoration="underline"
              {...linkProps}
            >
              {chunks}
            </Link>
          ),
          terms: (chunks) => (
            <Link
              href="https://policies.google.com/terms"
              isExternal
              rel="noopener noreferrer"
              target="_blank"
              textDecoration="underline"
              {...linkProps}
            >
              {chunks}
            </Link>
          ),
        }}
      />
    </Text>
  );
};

RecaptchaDisclosure.propTypes = {
  /** Extra props spread onto both Google policy/terms links. */
  linkProps: PropTypes.object,
};

export default RecaptchaDisclosure;
